import { useMemo, useState } from 'react';
import {
  CalendarClock,
  CalendarDays,
  FileText,
  Loader2,
  PawPrint,
  Star,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/ui/section-header';
import { StatusPill, type StatusTone } from '@/components/ui/status-pill';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { cn } from '@/lib/utils';
import { form_error_message } from '@/lib/forms';
import { use_my_bookings, use_cancel_booking } from '@/features/booking/hooks';
import type { Booking, BookingStatus } from '@/features/booking/types';
import { MODULE_LABEL } from '@/features/discovery/types';
import { MODULE_ICON } from '@/features/discovery/module-icon';
import { use_pets } from '@/features/pet/hooks';
import { use_my_reviews } from '@/features/review/hooks';
import { StarRating } from '@/components/ui/star-rating';
import { BookingRecordModal } from '@/components/booking-record-modal';
import { BookingDetailModal } from '@/components/booking-detail-modal';
import { WalkReportViewModal } from '@/components/walk-report-view-modal';
import { TrainingReportViewModal } from '@/components/training-report-view-modal';
import { GroomReportViewModal } from '@/components/groom-report-view-modal';
import { OwnerRescheduleModal } from '@/components/owner-reschedule-modal';
import { BookingSupportButton, MySupportRequests } from '@/components/booking-support-panel';
import { PendingPaymentCallout } from '@/components/pending-payment-callout';
import { OwnerWalkIncidents } from '@/components/owner-walk-incidents';
import { OwnerCremationTracker } from '@/components/owner-cremation-tracker';
import { OwnerLabTracker } from '@/components/owner-lab-tracker';
import { WalkPlansSection } from '@/components/walk-plans-section';
import { TrainingPackagesSection } from '@/components/training-packages-section';
import { ReviewModal } from '@/components/review-modal';

const IST = 'Asia/Kolkata';

const CLINICAL_MODULES = ['VET', 'CLINIC'];
function has_record(b: Booking): boolean {
  return b.status === 'COMPLETED' && CLINICAL_MODULES.includes(b.module_type);
}
/** A published walk report may exist once a WALKER walk is completed. */
function has_walk_report(b: Booking): boolean {
  return b.status === 'COMPLETED' && b.module_type === 'WALKER';
}
/** A published session report may exist once a TRAINER session is completed. */
function has_training_report(b: Booking): boolean {
  return b.status === 'COMPLETED' && b.module_type === 'TRAINER';
}
/** A published groom report (before/after) may exist once a GROOMER appointment is completed. */
function has_groom_report(b: Booking): boolean {
  return b.status === 'COMPLETED' && b.module_type === 'GROOMER';
}

const STATUS_TONE: Record<BookingStatus, StatusTone> = {
  PENDING_PAYMENT: 'warning',
  AWAITING_VENDOR: 'warning',
  CONFIRMED: 'info',
  IN_PROGRESS: 'info',
  COMPLETED: 'success',
  CANCELLED: 'destructive',
  NO_SHOW: 'destructive',
};


/**
 * Mirrors the USER rows of the backend transition table (`booking.transitions.ts`).
 * PENDING_PAYMENT is deliberately absent: only SYSTEM may cancel an unpaid
 * booking (the expiry cron does it). Offering the button there produced a
 * guaranteed 409 ILLEGAL_TRANSITION.
 */
const CANCELLABLE: BookingStatus[] = ['AWAITING_VENDOR', 'CONFIRMED'];
// Slot-based service bookings the owner can move to another open time. Dining
// (party/offering-based), cremation (on-demand) and commerce orders are excluded.
const RESCHEDULABLE_MODULES = ['VET', 'CLINIC', 'WALKER', 'TRAINER', 'GROOMER', 'LAB'];
const RESCHEDULABLE_STATUSES: BookingStatus[] = ['AWAITING_VENDOR', 'CONFIRMED'];
function can_reschedule(b: Booking): boolean {
  return RESCHEDULABLE_MODULES.includes(b.module_type) && RESCHEDULABLE_STATUSES.includes(b.status);
}
/**
 * Vendors set a reschedule-notice window; once the slot is nearer than that the
 * owner can no longer move the booking themselves. Shown as a disabled control
 * rather than a vanished one, so the customer knows the option existed and why
 * it closed. The server enforces the same rule (409 RESCHEDULE_WINDOW_CLOSED).
 */
function reschedule_window_closed(b: Booking): boolean {
  return !!b.reschedule_allowed_until && Date.parse(b.reschedule_allowed_until) <= Date.now();
}
function reschedule_closed_hint(b: Booking): string {
  if (!b.reschedule_allowed_until) return '';
  const hours = Math.round(
    (Date.parse(b.slot_start) - Date.parse(b.reschedule_allowed_until)) / 3_600_000,
  );
  // A zero-hour window means the provider allows changes right up to the slot,
  // so the only way it closes is the appointment time having arrived.
  if (hours <= 0) {
    return 'This appointment has already started, so it can no longer be rescheduled. Use Help to ask the provider.';
  }
  const window_label = hours === 1 ? 'an hour' : `${hours} hours`;
  return `Too close to the appointment to reschedule — this closes ${window_label} before the slot. Use Help to ask the provider.`;
}
const UPCOMING_STATUSES: BookingStatus[] = [
  'PENDING_PAYMENT',
  'AWAITING_VENDOR',
  'CONFIRMED',
  'IN_PROGRESS',
];

type Filter = 'all' | 'upcoming' | 'completed' | 'cancelled';
const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

/**
 * A live booking whose slot is already over.
 *
 * The provider accepted it and then never marked it complete or as a no-show, so the status
 * still reads CONFIRMED (or IN_PROGRESS) days later. Nothing about the record is wrong — the
 * booking really is in that state — but presenting it as something still to come is.
 */
function slot_has_passed(b: Booking): boolean {
  return UPCOMING_STATUSES.includes(b.status) && new Date(b.slot_end).getTime() < Date.now();
}

function in_filter(b: Booking, f: Filter): boolean {
  if (f === 'all') return true;
  /*
   * Upcoming means still to come, not merely "not finished". This checked the status alone,
   * so a restaurant table booked for 1pm last Tuesday still sat under Upcoming the following
   * week (CUSTOMER APP - 07) — and so did 45 walks, sessions and appointments.
   */
  if (f === 'upcoming') return UPCOMING_STATUSES.includes(b.status) && !slot_has_passed(b);
  if (f === 'completed') return b.status === 'COMPLETED';
  return b.status === 'CANCELLED' || b.status === 'NO_SHOW';
}

const ist_date = (iso: string) => new Date(iso).toLocaleDateString('en-CA', { timeZone: IST });
function day_tag(iso: string): string | null {
  const today = new Date();
  const todayStr = today.toLocaleDateString('en-CA', { timeZone: IST });
  const tomorrow = new Date(today.getTime() + 86_400_000).toLocaleDateString('en-CA', { timeZone: IST });
  const d = ist_date(iso);
  if (d === todayStr) return 'Today';
  if (d === tomorrow) return 'Tomorrow';
  return null;
}
function format_date(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', {
    timeZone: IST,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}
function format_time(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-IN', {
    timeZone: IST,
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function MyBookingsPage() {
  const bookings_q = use_my_bookings();
  const cancel_m = use_cancel_booking();
  const pets_q = use_pets();
  const reviews_q = use_my_reviews();
  const reviews = reviews_q.data ?? new Map();
  const [filter, set_filter] = useState<Filter>('all');
  const [cancel_error, set_cancel_error] = useState<string | null>(null);
  const [detail_booking_id, set_detail_booking_id] = useState<string | null>(null);
  const [record_booking_id, set_record_booking_id] = useState<string | null>(null);
  const [walk_report_booking_id, set_walk_report_booking_id] = useState<string | null>(null);
  const [training_report_booking_id, set_training_report_booking_id] = useState<string | null>(null);
  const [groom_report_booking_id, set_groom_report_booking_id] = useState<string | null>(null);
  const [review_booking, set_review_booking] = useState<Booking | null>(null);
  const [reschedule_booking, set_reschedule_booking] = useState<Booking | null>(null);

  const all_bookings = bookings_q.data ?? [];
  const pet_name = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of pets_q.data ?? []) map.set(p.id, p.name);
    return map;
  }, [pets_q.data]);

  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: 0, upcoming: 0, completed: 0, cancelled: 0 };
    for (const b of all_bookings) for (const f of FILTERS) if (in_filter(b, f.value)) c[f.value] += 1;
    return c;
  }, [all_bookings]);

  const visible = useMemo(() => {
    const list = all_bookings.filter((b) => in_filter(b, filter));
    // Upcoming soonest-first; everything else most-recent-first.
    const upcoming = filter === 'upcoming';
    return list.sort((a, b) =>
      upcoming
        ? a.slot_start.localeCompare(b.slot_start)
        : b.slot_start.localeCompare(a.slot_start),
    );
  }, [all_bookings, filter]);

  async function cancel(b: Booking) {
    set_cancel_error(null);
    if (!window.confirm('Cancel this booking?')) return;
    const reason = window.prompt('Reason (optional):') ?? '';
    try {
      await cancel_m.mutateAsync({ id: b.id, reason: reason.trim() || undefined });
    } catch (err) {
      // Without this the rejection is swallowed and the user sees nothing at
      // all — they just watch the booking stay put.
      set_cancel_error(form_error_message(err, 'Could not cancel this booking.'));
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader title="My bookings" description="Your service requests and their status." />

      <WalkPlansSection />
      <TrainingPackagesSection />

      {cancel_error && (
        <div
          className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          data-testid="cancel-booking-error"
          role="alert"
        >
          {cancel_error}
        </div>
      )}

      {bookings_q.isLoading ? (
        <Card>
          <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        </Card>
      ) : bookings_q.error ? (
        <ErrorState
          data-testid="my-bookings-error"
          title="Failed to load bookings"
          description={form_error_message(bookings_q.error, 'Please try again.')}
        />
      ) : all_bookings.length === 0 ? (
        <EmptyState
          data-testid="my-bookings-empty"
          icon={CalendarDays}
          title="No bookings yet"
          description="Find a service to get started — your requests will show up here."
        />
      ) : (
        <>
          {/* Status filter */}
          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Filter bookings" data-testid="booking-filters">
            {FILTERS.map((f) => {
              const active = filter === f.value;
              return (
                <button
                  key={f.value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => set_filter(f.value)}
                  data-testid={`booking-filter-${f.value}`}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-sm font-semibold transition-all duration-fast',
                    active
                      ? 'border-primary bg-primary/10 text-primary shadow-clay-sm'
                      : 'border-brand-outline-variant/20 bg-card text-muted-foreground hover:border-primary/30 hover:text-foreground',
                  )}
                >
                  {f.label}
                  <span
                    className={cn(
                      'rounded-full px-1.5 text-xs tabular-nums',
                      active ? 'bg-primary/15 text-primary' : 'bg-secondary text-muted-foreground',
                    )}
                  >
                    {counts[f.value]}
                  </span>
                </button>
              );
            })}
          </div>

          <MySupportRequests side="user" />

          {visible.length === 0 ? (
            <EmptyState
              data-testid="my-bookings-filter-empty"
              icon={CalendarDays}
              title={`No ${filter} bookings`}
              description="Try a different filter."
            />
          ) : (
            <Card>
              <ul className="divide-y-2 divide-brand-outline-variant/10" data-testid="my-bookings-list">
                {visible.map((b) => {
                  const Icon = MODULE_ICON[b.module_type] ?? PawPrint;
                  const tag = UPCOMING_STATUSES.includes(b.status) ? day_tag(b.slot_start) : null;
                  const pet = b.pet_id ? pet_name.get(b.pet_id) : null;
                  return (
                    <li
                      key={b.id}
                      className="flex flex-wrap items-center gap-3 p-4"
                      data-testid={`my-booking-${b.id}`}
                    >
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-button bg-primary/10 text-primary">
                        <Icon className="h-5 w-5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold">{MODULE_LABEL[b.module_type] ?? b.module_type}</span>
                          {pet && <span className="text-sm text-muted-foreground">for {pet}</span>}
                          {b.party_size ? <span className="text-sm text-muted-foreground">· party of {b.party_size}</span> : null}
                          <StatusPill tone={STATUS_TONE[b.status]}>
                            {b.status.replace(/_/g, ' ').toLowerCase()}
                          </StatusPill>
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                          <span className="font-medium text-foreground">{format_date(b.slot_start)}</span>
                          <span>· {format_time(b.slot_start)}</span>
                          {tag && (
                            <span className="rounded-full bg-info/12 px-2 py-0.5 text-xs font-semibold text-info">
                              {tag}
                            </span>
                          )}
                        </div>
                        {b.cancel_reason && (
                          <p className="mt-0.5 text-xs text-muted-foreground">Reason: {b.cancel_reason}</p>
                        )}
                        {/*
                          Said out loud rather than left to be inferred from a date. The status
                          pill still reads "confirmed", which is true, and on its own would let
                          someone assume the booking is still ahead of them.
                        */}
                        {slot_has_passed(b) && (
                          <p
                            className="mt-0.5 text-xs font-medium text-warning"
                            data-testid={`slot-passed-${b.id}`}
                          >
                            This slot has passed — waiting for the provider to confirm how it went.
                          </p>
                        )}
                        {can_reschedule(b) && reschedule_window_closed(b) && (
                          <p
                            className="mt-0.5 text-xs text-muted-foreground"
                            data-testid={`reschedule-closed-note-${b.id}`}
                          >
                            {reschedule_closed_hint(b)}
                          </p>
                        )}
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="font-heading text-base font-bold tabular-nums">₹{b.price_inr}</div>
                        {b.redeemable_against_bill && (
                          <div className="text-xs font-semibold text-success" data-testid={`booking-redeemable-${b.id}`}>Deducted from your bill</div>
                        )}
                        {b.payment && (
                          <div className="text-xs text-muted-foreground">
                            {b.payment.status === 'CAPTURED' ? 'Paid' : b.payment.status.toLowerCase()}
                          </div>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-0.5">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => set_detail_booking_id(b.id)}
                          data-testid={`view-details-${b.id}`}
                        >
                          <CalendarClock className="h-4 w-4" /> Details
                        </Button>
                        {has_record(b) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => set_record_booking_id(b.id)}
                            data-testid={`view-record-${b.id}`}
                          >
                            <FileText className="h-4 w-4" /> Visit record
                          </Button>
                        )}
                        {has_walk_report(b) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => set_walk_report_booking_id(b.id)}
                            data-testid={`view-walk-report-${b.id}`}
                          >
                            <FileText className="h-4 w-4" /> Walk report
                          </Button>
                        )}
                        {has_training_report(b) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => set_training_report_booking_id(b.id)}
                            data-testid={`view-training-report-${b.id}`}
                          >
                            <FileText className="h-4 w-4" /> Session report
                          </Button>
                        )}
                        {has_groom_report(b) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => set_groom_report_booking_id(b.id)}
                            data-testid={`view-groom-report-${b.id}`}
                          >
                            <FileText className="h-4 w-4" /> Groom report
                          </Button>
                        )}
                        {b.status === 'COMPLETED' &&
                          (reviews.get(b.id) ? (
                            <button
                              type="button"
                              onClick={() => set_review_booking(b)}
                              data-testid={`edit-review-${b.id}`}
                              className="flex items-center gap-1.5 rounded-button px-2 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                              aria-label="Edit your review"
                            >
                              <StarRating value={reviews.get(b.id)!.rating} size="sm" />
                              Edit
                            </button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => set_review_booking(b)}
                              data-testid={`rate-booking-${b.id}`}
                            >
                              <Star className="h-4 w-4" /> Rate visit
                            </Button>
                          ))}
                        {can_reschedule(b) &&
                          (reschedule_window_closed(b) ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled
                              title={reschedule_closed_hint(b)}
                              aria-label={reschedule_closed_hint(b)}
                              data-testid={`reschedule-closed-${b.id}`}
                            >
                              <CalendarClock className="h-4 w-4" /> Reschedule
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => set_reschedule_booking(b)}
                              data-testid={`reschedule-booking-${b.id}`}
                            >
                              <CalendarClock className="h-4 w-4" /> Reschedule
                            </Button>
                          ))}
                        {CANCELLABLE.includes(b.status) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => cancel(b)}
                            disabled={cancel_m.isPending}
                            data-testid={`cancel-booking-${b.id}`}
                          >
                            Cancel
                          </Button>
                        )}
                        <BookingSupportButton side="user" booking={b} />
                      </div>
                      {/*
                        Full-width, like the walk-OTP callout, because this is the single
                        thing standing between the customer and a confirmed booking — not a
                        peer of "Details" and "Reschedule".
                      */}
                      {b.status === 'PENDING_PAYMENT' && (
                        <PendingPaymentCallout booking={b} service_name={MODULE_LABEL[b.module_type] ?? b.module_type} />
                      )}
                      {b.active_walk_otp && (
                        <div
                          className="basis-full rounded-card border-2 border-primary/20 bg-primary/5 p-3"
                          data-testid={`walk-otp-callout-${b.id}`}
                        >
                          <p className="text-xs text-muted-foreground">
                            Share this code with your walker to{' '}
                            <span className="font-semibold text-foreground">
                              {b.active_walk_otp.kind === 'START' ? 'start' : 'finish'}
                            </span>{' '}
                            the walk
                          </p>
                          <div className="mt-1 flex items-center gap-3">
                            <span
                              className="font-heading text-2xl font-bold tracking-[0.3em] tabular-nums text-primary"
                              data-testid={`walk-otp-code-${b.id}`}
                            >
                              {b.active_walk_otp.code}
                            </span>
                            <span className="text-[11px] text-muted-foreground">Also sent to your phone by SMS</span>
                          </div>
                        </div>
                      )}
                      <OwnerWalkIncidents booking={b} />
                      <OwnerCremationTracker booking={b} />
                      <OwnerLabTracker booking={b} />
                    </li>
                  );
                })}
              </ul>
            </Card>
          )}
        </>
      )}

      {detail_booking_id && (
        <BookingDetailModal booking_id={detail_booking_id} on_close={() => set_detail_booking_id(null)} />
      )}

      {record_booking_id && (
        <BookingRecordModal booking_id={record_booking_id} on_close={() => set_record_booking_id(null)} />
      )}

      {walk_report_booking_id && (
        <WalkReportViewModal booking_id={walk_report_booking_id} on_close={() => set_walk_report_booking_id(null)} />
      )}
      {groom_report_booking_id && (
        <GroomReportViewModal booking_id={groom_report_booking_id} on_close={() => set_groom_report_booking_id(null)} />
      )}
      {training_report_booking_id && (
        <TrainingReportViewModal booking_id={training_report_booking_id} on_close={() => set_training_report_booking_id(null)} />
      )}

      {review_booking && (
        <ReviewModal
          booking_id={review_booking.id}
          existing={reviews.get(review_booking.id) ?? null}
          title={`${MODULE_LABEL[review_booking.module_type] ?? review_booking.module_type} · ${format_date(review_booking.slot_start)}`}
          on_close={() => set_review_booking(null)}
        />
      )}

      {reschedule_booking && (
        <OwnerRescheduleModal booking={reschedule_booking} on_close={() => set_reschedule_booking(null)} />
      )}
    </div>
  );
}
